import { ApolloError } from '@apollo/client';
import { logger } from '@core/logging';
import { LogCategory } from '@soteria/logger/browser';
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import { ErrorLayout } from './ErrorLayout';
import * as styles from './RouteErrorFallback.css';

/**
 * Checks whether the given error represents a timeout or abort scenario.
 * Handles both raw errors and ApolloError wrappers (where the AbortError
 * is nested inside `networkError`).
 */
function isTimeoutError(error: unknown): boolean {
  const checks = (err: Error): boolean => {
    const msg = err.message ?? '';
    const name = err.name ?? '';

    return [name === 'AbortError', name === 'TimeoutError', msg.includes('signal is aborted'), msg.includes('timed out'), msg.includes('timeout')].some(
      Boolean
    );
  };

  if (error instanceof ApolloError) {
    if (error.networkError && checks(error.networkError)) return true;

    return checks(error);
  }

  if (error instanceof Error) {
    return checks(error);
  }

  return false;
}

/**
 * Extracts the correlation ID from an error, checking the abort reason
 * chain (timeoutLink attaches it to the DOMException).
 */
function extractCorrelationId(error: unknown): string | undefined {
  const extractFromError = (err: Record<string, unknown>): string | undefined => {
    // Direct property (from our timeoutLink abort reason)
    if (typeof err.correlationId === 'string') return err.correlationId;

    // Check cause chain (AbortError wraps the DOMException)
    if (err.cause && typeof err.cause === 'object' && typeof (err.cause as Record<string, unknown>).correlationId === 'string') {
      return (err.cause as Record<string, unknown>).correlationId as string;
    }

    return undefined;
  };

  if (error instanceof ApolloError && error.networkError) {
    return extractFromError(error.networkError as unknown as Record<string, unknown>);
  }

  if (error instanceof Error) {
    return extractFromError(error as unknown as Record<string, unknown>);
  }

  return undefined;
}

/**
 * Route-level error fallback for React Router `errorElement`.
 *
 * Catches errors thrown during route loading/rendering, logs them to
 * App Insights, and displays a friendly ErrorLayout with appropriate
 * messaging for timeout vs generic errors.
 */
export function RouteErrorFallback() {
  const error = useRouteError();
  const isTimeout = isTimeoutError(error);
  const correlationId = extractCorrelationId(error);

  useEffect(() => {
    const errorDetails =
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
          }
        : String(error);

    logger.error('Route error caught', {
      category: LogCategory.ERROR,
      action: 'route_error_fallback',
      errorType: isTimeout ? 'timeout' : 'unknown',
      correlationId,
      error: errorDetails,
      url: window.location.href,
    });
  }, [error, isTimeout, correlationId]);

  if (isTimeout) {
    return (
      <ErrorLayout
        actionText="Try Again"
        message="The server took too long to respond. Please try again."
        title="Request Timed Out"
        onAction={() => window.location.reload()}
      >
        {correlationId && <p className={styles.correlationId}>Correlation ID: {correlationId}</p>}
      </ErrorLayout>
    );
  }

  return (
    <ErrorLayout
      actionText="Try Again"
      message="An error occurred while loading this page."
      title="Something went wrong"
      onAction={() => window.location.reload()}
    >
      {correlationId && <p className={styles.correlationId}>Correlation ID: {correlationId}</p>}
    </ErrorLayout>
  );
}
